import React, { useReducer } from 'react';
import { twMerge } from 'tailwind-merge';
import { sitInRest, sitOutRest } from '@/services/table/tableService';
import Checkbox from '@/components/new-design/Checkbox';
import { useToast } from '@/components/toast/useToast';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import buttonStyles from '@/components/new-design/Button.module.css';
import { Combobox } from '@headlessui/react';

import styles from './SitOutButton.module.css';
import ChevronDownIcon from '@/components/ChevronDownIcon';
import ChevronUpIcon from '@/components/ChevronUpIcon';

interface SitOutButtonProps {
  tableId: string;
  disabled?: boolean;
}

type ActionType = { type: 'TOGGLE_CHECKBOX' } | { type: 'SET_OPTION'; payload: string };

interface StateType {
  isChecked: boolean;
  selectedOption: string;
}

const initialState: StateType = {
  isChecked: false,
  selectedOption: 'Sit Out Next Hand',
};

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'TOGGLE_CHECKBOX':
      return { ...state, isChecked: !state.isChecked };
    case 'SET_OPTION':
      return { ...state, selectedOption: action.payload };
    default:
      return state;
  }
};

const SitOutButton: React.FC<SitOutButtonProps> = ({ tableId, disabled }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const tableState = useSelector((state: ReducerState) => state.tables?.[tableId]);

  const { toast } = useToast();

  const handleOnClickSitOutTable = async () => {
    try {
      if (state.isChecked) {
        await sitInRest({ tableId, tableUrl: tableState?.url });
      } else {
        await sitOutRest({ tableId, tableUrl: tableState?.url });
      }
      dispatch({ type: 'TOGGLE_CHECKBOX' });
    } catch (_error) {
      toast({ description: 'Error sitting out the table' });
      alert('Error when leaving table');
    }
  };

  const handleSelectChange = (value: string) => {
    dispatch({ type: 'SET_OPTION', payload: value });
  };

  return (
    <Combobox value={state.selectedOption} onChange={handleSelectChange} disabled={disabled}>
      {({ open, disabled }) => (
        <div
          className={twMerge(
            styles.sitOutSelect,
            buttonStyles.pillWithBorder,
            open && styles.open,
            state.isChecked && styles.checked,
            disabled && styles.disabled,
          )}
        >
          <div className="relative flex w-full">
            <Checkbox
              text={state.selectedOption}
              checked={state.isChecked}
              onChange={handleOnClickSitOutTable}
              className="text-white hover:cursor-pointer"
              disabled={disabled}
            />
            <Combobox.Button className="-mr-4 flex items-center p-2 pr-4 text-ph-white-30">
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Combobox.Button>
          </div>
          <Combobox.Options className={styles.sitOutOptions}>
            <Combobox.Option value="Sit Out Next Hand" className="cursor-pointer text-sm">
              Sit Out Next Hand
            </Combobox.Option>
            {/* <Combobox.Option value="Sit Out Next BB">
            Sit Out Next BB
          </Combobox.Option>
          <Combobox.Option value="Sit Out Next Hand All Tables">
            Sit Out Next Hand All Tables
          </Combobox.Option>
          <Combobox.Option value="Sit Out Next BB All Tables">
            Sit Out Next BB All Tables
          </Combobox.Option> */}
          </Combobox.Options>
        </div>
      )}
    </Combobox>
  );
};

export default SitOutButton;
